import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getCollectionCallOutcomes, getInternalUsers } from 'services'
import { Modal } from 'stories/components'
import { InputConvert, InputValidate, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { type ICollectionCall, CollectionCallOutcome, outcomeLabels, ptpStatusLabels } from './types'

const today = () => new Date().toISOString().slice(0, 10)

/** Local wall-clock HH:mm, which is what an <input type="time"> expects. */
const now = () => {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export const defaultInputs = (): Record<string, InputType> => ({
  callDate: {
    inputType: 'text',
    type: 'date',
    title: 'Call Date',
    value: today(),
    required: true,
  },
  callTime: {
    inputType: 'text',
    type: 'time',
    title: 'Call Time',
    // A call is logged as it happens, so the clock is the answer nearly every time. Editing a past
    // call keeps its stored time: the merge below overwrites this.
    value: now(),
    required: false,
  },
  direction: {
    inputType: 'select',
    title: 'Direction',
    options: { outbound: 'Outbound', inbound: 'Inbound' },
    hasDefaultOption: false,
    value: 'outbound',
    required: true,
  },
  contactName: {
    inputType: 'text',
    type: 'text',
    title: 'Spoke With',
    required: false,
  },
  contactPhone: {
    inputType: 'text',
    type: 'phone',
    title: 'Phone Number',
    required: false,
  },
  outcome: {
    inputType: 'select',
    title: 'Outcome',
    options: outcomeLabels,
    hasDefaultOption: true,
    defaultOptionText: 'Select an outcome',
    value: '',
    // The whole point of the screen: no call is logged without one.
    required: true,
  },
  callbackDate: {
    inputType: 'text',
    type: 'date',
    title: 'Callback Date',
    required: false,
    visible: false,
  },
  ptpDate: {
    inputType: 'text',
    type: 'date',
    title: 'PTP Date',
    required: true,
    visible: false,
  },
  ptpAmount: {
    inputType: 'text',
    type: 'thousandSep',
    title: 'PTP Amount',
    prefix: '$',
    required: true,
    visible: false,
  },
  employee: {
    inputType: 'select',
    title: 'Employee Name',
    // Filled from the tenant's internal users; blank until that lands so a slow fetch cannot drop
    // the name on a call being edited.
    options: {},
    hasDefaultOption: true,
    defaultOptionText: 'Select an employee',
    tooltip: 'Who made the call. Defaults to you.',
    value: '',
    required: true,
  },
  ptpStatus: {
    inputType: 'select',
    title: 'Promise Status',
    options: ptpStatusLabels,
    hasDefaultOption: false,
    required: false,
    visible: false,
  },
  notes: {
    inputType: 'textarea',
    type: 'text',
    title: 'Notes',
    span: 'full',
    required: false,
  },
})

/**
 * Log one collections call.
 *
 * Who made the call and when are asked for every call and answered before you start: the employee
 * defaults to you, picked from the tenant's own user list rather than typed, and the time defaults
 * to the clock. A log people fill in by hand is a log that drifts.
 *
 * The outcome drives the rest of the form: a Promise to Pay opens the two fields that make a
 * promise checkable (date and amount) and makes them required, a callback opens its date, and
 * everything else asks for neither. Doing it this way rather than showing all of them all the time
 * is what keeps a PTP from being saved half-filled, which is the one shape of this record the
 * nightly sweep cannot act on.
 */
export const AddCollectionCall = ({
  item,
  prefill,
  onClose,
  onUpdate,
}: {
  item: ICollectionCall | null
  /**
   * Values a call placed from the app already knows: who was rung, on what number, and which dial
   * this is the write-up of. The collector supplies the outcome, which is the only part a machine
   * cannot observe.
   */
  prefill?: Record<string, any>
  onClose: Function
  onUpdate: Function
}) => {
  const auth = useSelector((state: any) => state.auth)
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())

  // The outcome list is served by the API so a value added on the server appears here without a
  // frontend release; the local labels are the fallback until it lands.
  useEffect(() => {
    getCollectionCallOutcomes()
      .then((outcomes) => {
        if (!Array.isArray(outcomes) || !outcomes.length) return
        setInputs((prev) => {
          const newInputs = cloneDeep(prev)
          newInputs.outcome.options = outcomes.reduce(
            (map: Record<string, string>, o) => ({ ...map, [o.value]: o.label }),
            {},
          )
          return newInputs
        })
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    setInputs((prev) => {
      const newInputs = cloneDeep(prev)
      if (item) {
        Object.keys(newInputs).forEach((key) => {
          const value = (item as any)[key]
          newInputs[key].value = value === null || value === undefined ? '' : value
        })
      } else {
        // A new call belongs to whoever is logging it until they say otherwise.
        newInputs.employee.value = auth?.profile?.name || auth?.profile?.email || ''
        Object.keys(prefill || {}).forEach((key) => {
          if (newInputs[key] && prefill?.[key] !== undefined && prefill?.[key] !== '')
            newInputs[key].value = prefill[key]
        })
      }
      // Seed the picker with the name it is already showing, so the field reads correctly on the
      // first paint instead of sitting on "Select an employee" until the user list arrives.
      const employee = String(newInputs.employee.value || '')
      if (employee) newInputs.employee.options = { [employee]: employee }
      applyOutcome(newInputs, String(newInputs.outcome.value || ''), !item)
      return newInputs
    })
  }, [item])

  /**
   * The employee list is the tenant's own internal users, so a promise is attributed to somebody
   * who actually exists and the queue can be filtered by a name that is spelled one way.
   *
   * Whatever the row already carries is kept in the list even when the fetch does not return it,
   * so editing an old call, or one taken by somebody who has since left, cannot quietly reassign it.
   */
  useEffect(() => {
    getInternalUsers()
      .then((users) => {
        const names = (users || []).map((u) => u.name || u.email).filter(Boolean)
        setInputs((prev) => {
          const newInputs = cloneDeep(prev)
          const current = String(newInputs.employee.value || '')
          if (current && !names.includes(current)) names.push(current)
          newInputs.employee.options = names.sort().reduce((map: Record<string, string>, n) => ({ ...map, [n]: n }), {})
          return newInputs
        })
      })
      .catch(() => {})
  }, [])

  /** Show and require only the fields the chosen outcome actually needs. */
  const applyOutcome = (newInputs: Record<string, InputType>, outcome: string, isNew: boolean) => {
    const isPromise = outcome === CollectionCallOutcome.PromiseToPay
    ;['ptpDate', 'ptpAmount'].forEach((key) => {
      newInputs[key].visible = isPromise
      newInputs[key].required = isPromise
      if (!isPromise) newInputs[key].error = ''
    })
    // The status is the servicer's own call (withdraw a promise, put one back); kept and broken
    // are decided by the sweep, so there is nothing to set on a promise being created.
    newInputs.ptpStatus.visible = isPromise && !isNew
    newInputs.callbackDate.visible = outcome === CollectionCallOutcome.CallbackRequested
    if (!newInputs.callbackDate.visible) newInputs.callbackDate.error = ''
  }

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].value = value
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    if (key === 'outcome') applyOutcome(newInputs, String(value || ''), !item)
    setInputs(newInputs)
  }

  const onSubmit = () => {
    let hasError = false
    const newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newInputs) {
      const input = newInputs[key]
      if (input.visible === false) continue
      input.error = InputValidate(input)
      if (input.error) hasError = true
      data[key] = input.value
    }
    setInputs(newInputs)
    if (hasError) return

    data.ptpAmount = data.ptpAmount === undefined ? undefined : Number(removeComma(data.ptpAmount))
    // Ties the write-up to the dial, so the log can show what the carrier reported alongside what
    // was agreed.
    if (prefill?.phoneCallId) data.phoneCallId = prefill.phoneCallId
    setLoading(true)
    onUpdate(data)
  }

  return (
    <Modal
      title={item ? 'Update Collections Call' : 'Log Collections Call'}
      titleOkay="Save"
      loading={loading}
      onClose={() => onClose()}
      onOk={onSubmit}
      childLevel={2}
      isOpen
    >
      <div className="text-gray-900 w-144 grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-2">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          if (input.visible === false) return null
          return (
            <div className={`input md:col-span-${input.span || 1}`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
    </Modal>
  )
}
